import React, {Component} from 'react';
import {connect} from 'react-redux';
import debounce from 'lodash/debounce';
import Chip from '@material-ui/core/Chip';
import Alert from '@material-ui/lab/Alert';
import Auth from '../../auth/auth';
import AssignmentTable from './components/assignment-table';
import AssignmentDrawer from './components/assignment-drawer';
import EffectiveAccessPanel from './components/effective-access-panel';
import AuditLog from './components/audit-log';
import RosterTable from './components/roster-table';
import ReviewPanel from './components/review-panel';
import NominationPanel from './components/nomination-panel';
import Button from '../../components/button';
import {
  selectSubject, loadAudit, loadRoles, loadScopes, loadRoster, loadReview,
  openGrantDrawer, closeGrantDrawer, createAssignment, revokeAssignment,
  loadNominations, activateNomination,
} from './access-admin.actions';
import './access-admin.scss';

const auth = new Auth();
const ROSTER_DEBOUNCE_MS = 300;

/**
 * The access admin surface (docs/access/slice-4b-admin-surface-plan.md §5). Three panels —
 * assignments, effective access, audit — plus the grant drawer. No standalone role-list panel:
 * the drawer's role selector already expands to show chains + permissions.
 *
 * Rendered `<AccessAdmin embedded />` from `SuperUserPanel`, like `MyDelegations` (§3.2).
 */
class AccessAdmin extends Component {
  // One request per keystroke was the original wiring; debounced here rather than in the action
  // creator so the raw dispatcher stays trivially testable and reusable.
  reloadRosterDebounced = debounce((filters) => this.props.loadRoster(filters), ROSTER_DEBOUNCE_MS);

  componentDidMount() {
    // The roster loads on mount. 4b loaded roles and audit but never subjects, so the page's own
    // picker showed "No options" until someone typed — the emptiest possible first impression of a
    // screen whose job is to show who holds what.
    this.props.loadRoster();
    this.props.loadRoles();
    this.props.loadAudit();
    this.props.loadReview();
    // Slice 4c — S7's activation view. See NominationPanel's header for why it is on this page.
    this.props.loadNominations();
  }

  componentWillUnmount() {
    this.reloadRosterDebounced.cancel();
  }

  handleFilterChange = (change) => {
    const next = {...this.props.rosterFilters, ...change};
    // The select filters should feel immediate; only free text is debounced.
    if ('q' in change) this.reloadRosterDebounced(next);
    else this.props.loadRoster(next);
  };

  /** A roster row's Manage action opens that subject's detail below the table. */
  handleManage = (person) => {
    this.props.selectSubject({id: person.subjectId, display_name: person.displayName,
      subject_type: person.subjectType});
  };

  handleCloseDetail = () => this.props.selectSubject(null);

  handleAuditFilterChange = (change) => {
    const next = {...this.props.auditFilters, ...change};
    // An absent or false value means "chip off" — dropped rather than sent, or it reaches the
    // query string as the literal text `undefined`, which the server rejects as an invalid date.
    Object.keys(next).forEach((k) => (next[k] === undefined || next[k] === false) && delete next[k]);
    this.props.loadAudit(next);
  };

  shadowLabel() {
    const enforcement = auth.getAcEnforcement();
    if (enforcement.mode === 'all') return 'enforced';
    if (enforcement.mode === 'modules' && enforcement.modules.length) {
      return `enforced: ${enforcement.modules.join(', ')}`;
    }
    return 'shadow mode — hints not enforced';
  }

  render() {
    const {
      currentSubject, assignments, canDo, cannotDo, audit, auditFilters, review, roles, scopeOptions, roster, rosterFilters,
      grantDrawerOpen, grantError,
    } = this.props;

    const wrapperClass = this.props.embedded ? 'sub-module access-admin' : 'app-content-container access-admin';

    return (
      <div className={wrapperClass}>
        <div className="page-header">
          <h2>Access Administration</h2>
          <Chip size="small" label={this.shadowLabel()} className="shadow-chip" data-testid="ac-shadow-chip" />
        </div>

        {/*
          §3.7 — the admin's OWN subject could not be resolved. Shown here rather than left to be
          discovered on a failed grant: every 4a write path refuses NO_ACTOR_SUBJECT for this
          caller, so without the banner the page looks fully functional and every Grant silently
          403s with no explanation on screen. It is the admin's own state, not the selected
          subject's — everyone else's is caught by scripts/access/check-grant-coverage.js.
        */}
        {!auth.getAcSubjectResolved() ? (
          // MUI Alert, not components/banner.js: that one is dismissible and needs a
          // dismissBanner handler, and this state must not be dismissable — it does not go away
          // until a subject is provisioned. Alert also carries its own styling, so this adds no
          // SCSS class (§3.8's enumerated-selector-list footgun cannot bite what has no class).
          <Alert severity="warning" data-testid="ac-actor-unresolved">
            Your own account has no active access subject, so every grant and revoke on this page
            will be refused. Run <code>scripts/access/check-grant-coverage.js</code> and have a
            subject provisioned before administering access.
          </Alert>
        ) : null}

        {/*
          S1 IS THE PAGE, not a panel on it. 4b put a search box here, so the admin home showed
          nothing until you typed a name you had to already know — and the picker rendered
          "No options" before the first keystroke, which reads as "there are no subjects".
        */}
        <RosterTable
          people={roster.people}
          counts={roster.counts}
          total={roster.total}
          expiring={roster.expiring}
          filters={rosterFilters}
          onFilterChange={this.handleFilterChange}
          onManage={this.handleManage}
          onGrant={this.props.openGrantDrawer}
        />

        {currentSubject && (
          <div className="subject-detail" data-testid="ac-subject-detail">
            <h3 className="detail-heading">
              {currentSubject.display_name}
              <Button label="Close" className="btn btn-link" onClick={this.handleCloseDetail}
                data-testid="ac-detail-close" />
            </h3>
            <AssignmentTable
              assignments={assignments}
              onGrant={this.props.openGrantDrawer}
              onRevoke={(assignmentId) => this.props.revokeAssignment(assignmentId, currentSubject.id)}
            />
            <EffectiveAccessPanel canDo={canDo} cannotDo={cannotDo} />
          </div>
        )}

        <AuditLog
          events={audit}
          filters={auditFilters}
          onFilterChange={this.handleAuditFilterChange}
        />

        <ReviewPanel findings={review} />

        {/* Slice 4c — mockup S7's activation half. HERE rather than on /settings/delegations
            because Q7 assigns it to platform_admin, and secured-layout redirects any other
            /settings/* path when getContext() is null, which is a platform admin's normal state. */}
        <NominationPanel
          nominations={this.props.nominations}
          error={this.props.activateError}
          onActivate={this.props.activateNomination}
        />

        <AssignmentDrawer
          open={grantDrawerOpen}
          onClose={this.props.closeGrantDrawer}
          roles={roles}
          scopeOptions={scopeOptions}
          subjectId={currentSubject && currentSubject.id}
          subjectName={currentSubject && currentSubject.display_name}
          grantError={grantError}
          onLoadScopes={this.props.loadScopes}
          onSubmit={this.props.createAssignment}
        />
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  roster: state.accessAdminReducer.roster,
  rosterFilters: state.accessAdminReducer.rosterFilters,
  currentSubject: state.accessAdminReducer.currentSubject,
  assignments: state.accessAdminReducer.assignments,
  canDo: state.accessAdminReducer.canDo,
  cannotDo: state.accessAdminReducer.cannotDo,
  audit: state.accessAdminReducer.audit,
  auditFilters: state.accessAdminReducer.auditFilters,
  review: state.accessAdminReducer.review,
  roles: state.accessAdminReducer.roles,
  scopeOptions: state.accessAdminReducer.scopeOptions,
  grantDrawerOpen: state.accessAdminReducer.grantDrawerOpen,
  grantError: state.accessAdminReducer.grantError,
  nominations: state.accessAdminReducer.nominations,
  activateError: state.accessAdminReducer.activateError,
});

const mapDispatchToProps = {
  selectSubject, loadAudit, loadRoles, loadScopes, loadRoster, loadReview,
  openGrantDrawer, closeGrantDrawer, createAssignment, revokeAssignment,
  loadNominations, activateNomination,
};

export default connect(mapStateToProps, mapDispatchToProps)(AccessAdmin);
